import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Store, User, Mail, Lock, Phone, MapPin, Globe, FileText, Building, CreditCard, Truck, ExternalLink, Upload, X, FileCheck, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MERCHANT_TYPES = [
  {
    id: 'affiliate',
    name: 'Affiliate Marketer',
    description: 'Add affiliate links from Amazon, Temu, CJ, ShareASale, etc.',
    icon: ExternalLink,
    color: 'from-blue-500 to-cyan-500',
    autoApprove: true
  },
  {
    id: 'dropshipper',
    name: 'Dropshipper',
    description: 'Connect your supplier APIs (Alibaba, CJ Dropshipping, etc.)',
    icon: Truck,
    color: 'from-purple-500 to-pink-500',
    autoApprove: false
  },
  {
    id: 'direct',
    name: 'Direct Seller',
    description: 'Sell your own physical products with your own inventory',
    icon: Store,
    color: 'from-orange-500 to-red-500',
    autoApprove: false
  }
];

// Document types required for verification
const REQUIRED_DOCUMENTS = {
  dropshipper: [
    { id: 'id_document', name: 'Government ID', description: 'Passport, National ID, or Driver\'s License' },
    { id: 'business_registration', name: 'Business Registration', description: 'Certificate of incorporation or business license' },
    { id: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement (last 3 months)' }
  ],
  direct: [
    { id: 'id_document', name: 'Government ID', description: 'Passport, National ID, or Driver\'s License' },
    { id: 'business_registration', name: 'Business Registration', description: 'Certificate of incorporation or business license' },
    { id: 'proof_of_address', name: 'Proof of Address', description: 'Utility bill or bank statement (last 3 months)' },
    { id: 'product_certification', name: 'Product Certification (Optional)', description: 'Quality certificates for your products if applicable' }
  ]
};

export default function MerchantRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const fileInputRefs = useRef({});
  
  const [formData, setFormData] = useState({
    // Step 1: Business Type
    business_type: '',
    
    // Step 2: Account Info
    email: '',
    password: '',
    confirmPassword: '',
    
    // Step 3: Business Info
    business_name: '',
    company_name: '',
    tax_id: '',
    contact_name: '',
    phone: '',
    whatsapp: '',
    
    // Step 4: Address
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: '',
    
    // Step 5: Additional Info (or Step 6 if documents required)
    website_url: '',
    description: '',
    
    // Step 5 for non-affiliates: Document Verification
    documents: {}
  });
  
  // Check if document verification is required
  const requiresDocuments = formData.business_type && formData.business_type !== 'affiliate';
  const totalSteps = requiresDocuments ? 6 : 5;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle document upload
  const handleDocumentUpload = async (docId, file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, PDF, or WebP files.');
      return;
    }
    
    // Validate file size (10MB max for documents)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }
    
    setUploadingDoc(docId);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('document_type', docId);
      
      const response = await fetch(`${API_URL}/api/uploads`, {
        method: 'POST',
        body: uploadFormData
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const data = await response.json();
      const documentUrl = `${API_URL}${data.full_url}`;
      
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [docId]: {
            url: documentUrl,
            filename: file.name,
            uploaded_at: new Date().toISOString()
          }
        }
      }));
      
      toast.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error(error.message || 'Failed to upload document. Please try again.');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Remove uploaded document
  const removeDocument = (docId) => {
    setFormData(prev => {
      const newDocs = { ...prev.documents };
      delete newDocs[docId];
      return { ...prev, documents: newDocs };
    });
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.business_type) {
          toast.error('Please select a merchant type');
          return false;
        }
        break;
      case 2:
        if (!formData.email || !formData.password) {
          toast.error('Please fill in email and password');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error('Passwords do not match');
          return false;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          return false;
        }
        break;
      case 3:
        if (!formData.business_name || !formData.contact_name || !formData.phone) {
          toast.error('Please fill in all required fields');
          return false;
        }
        break;
      case 4:
        if (!formData.address || !formData.city || !formData.country || !formData.postal_code) {
          toast.error('Please fill in all required address fields');
          return false;
        }
        break;
      case 5:
        if (requiresDocuments) {
          // Document verification step - check required documents
          const requiredDocs = REQUIRED_DOCUMENTS[formData.business_type] || [];
          const mandatoryDocs = requiredDocs.filter(d => !d.name.includes('Optional'));
          const missingDocs = mandatoryDocs.filter(d => !formData.documents[d.id]);
          
          if (missingDocs.length > 0) {
            toast.error(`Please upload: ${missingDocs.map(d => d.name).join(', ')}`);
            return false;
          }
        } else {
          // Business description step for affiliates
          if (!formData.description) {
            toast.error('Please provide a business description');
            return false;
          }
        }
        break;
      case 6:
        // Business description step for non-affiliates
        if (!formData.description) {
          toast.error('Please provide a business description');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/merchant/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          business_name: formData.business_name,
          business_type: formData.business_type,
          company_name: formData.company_name || null,
          tax_id: formData.tax_id || null,
          contact_name: formData.contact_name,
          phone: formData.phone,
          whatsapp: formData.whatsapp || null,
          address: formData.address,
          city: formData.city,
          state: formData.state || '',
          country: formData.country,
          postal_code: formData.postal_code,
          website_url: formData.website_url || null,
          description: formData.description,
          // Include documents for verification
          verification_documents: Object.keys(formData.documents).length > 0 ? formData.documents : null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      // Store token
      localStorage.setItem('merchant_token', data.access_token);
      localStorage.setItem('merchant_data', JSON.stringify(data.merchant));

      toast.success(data.message);
      navigate('/merchant/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white font-bold text-2xl mb-4">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-sm">SS</span>
            </div>
            SALESSPACE
          </Link>
          <h1 className="text-3xl font-bold text-white mt-4">Become a Merchant</h1>
          <p className="text-slate-400 mt-2">Start selling on SALESSPACE marketplace</p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-between mb-8" data-testid="merchant-progress-steps">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div key={s} className="flex items-center">
              <div data-testid={`merchant-progress-step-${s}`} className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                s < step ? 'bg-green-500 text-white' :
                s === step ? 'bg-blue-500 text-white' :
                'bg-slate-700 text-slate-400'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < totalSteps && (
                <div className={`w-full h-1 mx-2 ${s < step ? 'bg-green-500' : 'bg-slate-700'}`} style={{ width: '40px' }} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-8">
          
          {/* Step 1: Merchant Type */}
          {step === 1 && (
            <div data-testid="merchant-register-step-1">
              <h2 className="text-xl font-semibold text-white mb-6">What type of merchant are you?</h2>
              <div className="space-y-4">
                {MERCHANT_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.id}
                      data-testid={`merchant-type-${type.id}`}
                      onClick={() => updateField('business_type', type.id)}
                      className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                        formData.business_type === type.id
                          ? 'border-blue-500 bg-blue-500/10'
                          : 'border-slate-600 hover:border-slate-500'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg bg-gradient-to-br ${type.color}`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{type.name}</h3>
                            {type.autoApprove && (
                              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">
                                Instant Approval
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-400 mt-1">{type.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 2: Account Info */}
          {step === 2 && (
            <div data-testid="merchant-register-step-2">
              <h2 className="text-xl font-semibold text-white mb-6">Create your account</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email Address *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="email"
                      data-testid="merchant-email"
                      value={formData.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      data-testid="merchant-password"
                      value={formData.password}
                      onChange={(e) => updateField('password', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Min 8 characters"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="password"
                      data-testid="merchant-confirm-password"
                      value={formData.confirmPassword}
                      onChange={(e) => updateField('confirmPassword', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Repeat password"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Business Info */}
          {step === 3 && (
            <div data-testid="merchant-register-step-3">
              <h2 className="text-xl font-semibold text-white mb-6">Business Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Business/Store Name *</label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="merchant-business-name"
                      value={formData.business_name}
                      onChange={(e) => updateField('business_name', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Your Store Name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Company Name</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => updateField('company_name', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        placeholder="Legal company name"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Tax ID</label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) => updateField('tax_id', e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        placeholder="VAT/Tax number"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Contact Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="merchant-contact-name"
                      value={formData.contact_name}
                      onChange={(e) => updateField('contact_name', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Your full name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone Number *</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      data-testid="merchant-phone"
                      value={formData.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">WhatsApp Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="tel"
                      data-testid="merchant-whatsapp"
                      value={formData.whatsapp}
                      onChange={(e) => updateField('whatsapp', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Include country code, e.g. +234 801 234 5678"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Customers tap "Chat on WhatsApp" on your products to reach you. Leave blank to use your phone number.</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Address */}
          {step === 4 && (
            <div data-testid="merchant-register-step-4">
              <h2 className="text-xl font-semibold text-white mb-6">Business Address</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Street Address *</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      data-testid="merchant-address"
                      value={formData.address}
                      onChange={(e) => updateField('address', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="123 Main Street"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">City *</label>
                    <input
                      type="text"
                      data-testid="merchant-city"
                      value={formData.city}
                      onChange={(e) => updateField('city', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">State/Province</label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => updateField('state', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="State"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Country *</label>
                    <input
                      type="text"
                      data-testid="merchant-country"
                      value={formData.country}
                      onChange={(e) => updateField('country', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="Country"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Postal Code *</label>
                    <input
                      type="text"
                      data-testid="merchant-postal"
                      value={formData.postal_code}
                      onChange={(e) => updateField('postal_code', e.target.value)}
                      className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="12345"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Document Verification (only for non-affiliates) OR Business Description (for affiliates) */}
          {step === 5 && requiresDocuments && (
            <div data-testid="merchant-register-step-5-docs">
              <h2 className="text-xl font-semibold text-white mb-2">Document Verification</h2>
              <p className="text-slate-400 text-sm mb-6">
                Upload the required documents for identity verification. This helps us maintain a secure marketplace.
              </p>
              
              {/* Warning Banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-400 mb-1">Review Required</h4>
                    <p className="text-sm text-slate-300">
                      Your documents will be reviewed by our team. This usually takes 1-2 business days.
                      You&apos;ll be notified once your account is approved.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                {(REQUIRED_DOCUMENTS[formData.business_type] || []).map((doc) => {
                  const isUploaded = formData.documents[doc.id];
                  const isOptional = doc.name.includes('Optional');
                  const isUploading = uploadingDoc === doc.id;
                  
                  return (
                    <div 
                      key={doc.id}
                      className={`p-4 rounded-lg border transition-all ${
                        isUploaded 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-slate-600 bg-slate-900/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-white">
                              {doc.name}
                              {!isOptional && <span className="text-red-400 ml-1">*</span>}
                            </h4>
                            {isUploaded && (
                              <FileCheck className="w-4 h-4 text-green-400" />
                            )}
                          </div>
                          <p className="text-sm text-slate-400 mt-1">{doc.description}</p>
                          
                          {isUploaded && (
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-green-400 bg-green-500/20 px-2 py-1 rounded">
                                ✓ {formData.documents[doc.id].filename}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeDocument(doc.id)}
                                className="text-xs text-red-400 hover:text-red-300"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <input
                            ref={el => fileInputRefs.current[doc.id] = el}
                            type="file"
                            accept="image/jpeg,image/png,application/pdf,image/webp"
                            onChange={(e) => handleDocumentUpload(doc.id, e.target.files?.[0])}
                            className="hidden"
                            data-testid={`doc-upload-${doc.id}`}
                          />
                          <button
                            type="button"
                            data-testid={`doc-upload-btn-${doc.id}`}
                            onClick={() => fileInputRefs.current[doc.id]?.click()}
                            disabled={isUploading}
                            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                              isUploaded
                                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                : 'bg-blue-600 text-white hover:bg-blue-500'
                            }`}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Uploading...
                              </>
                            ) : isUploaded ? (
                              <>
                                <Upload className="w-4 h-4" />
                                Replace
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Upload
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <p className="text-xs text-slate-500 mt-4">
                Accepted formats: JPG, PNG, PDF, WebP · Max 10MB per file
              </p>
            </div>
          )}
          
          {/* Step 5 for affiliates OR Step 6 for others: Business Description */}
          {((step === 5 && !requiresDocuments) || (step === 6 && requiresDocuments)) && (
            <div data-testid="merchant-register-step-description">
              <h2 className="text-xl font-semibold text-white mb-6">Tell us about your business</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Website URL</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="url"
                      value={formData.website_url}
                      onChange={(e) => updateField('website_url', e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Business Description *</label>
                  <div className="relative">
                    <FileText className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                    <textarea
                      data-testid="merchant-description"
                      value={formData.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      rows={4}
                      className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-blue-500 focus:outline-none resize-none"
                      placeholder="Describe your business, products you sell, and your experience..."
                    />
                  </div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <h4 className="font-medium text-blue-400 mb-2">Commission Structure</h4>
                  <p className="text-sm text-slate-300">
                    SALESSPACE charges a <strong className="text-white">10% commission</strong> on each sale made through your products.
                    This helps us maintain the platform and provide support.
                  </p>
                </div>
                
                {requiresDocuments && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <h4 className="font-medium text-amber-400 mb-2">What happens next?</h4>
                    <p className="text-sm text-slate-300">
                      After submitting, our team will review your documents within 1-2 business days.
                      You&apos;ll receive an email notification once your account is approved.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <button
                onClick={prevStep}
                className="px-6 py-3 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
              >
                Back
              </button>
            ) : (
              <Link
                to="/merchant/login"
                className="px-6 py-3 text-slate-400 hover:text-white transition-colors"
              >
                Already have an account?
              </Link>
            )}
            
            {step < totalSteps ? (
              <button
                onClick={nextStep}
                data-testid="merchant-next-step"
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-500 hover:to-purple-500 transition-all font-medium"
              >
                Continue
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                data-testid="merchant-submit"
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-500 hover:to-emerald-500 transition-all font-medium disabled:opacity-50"
              >
                {loading ? 'Creating Account...' : 'Create Merchant Account'}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-500 text-sm mt-6">
          By registering, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
